import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import Navbar from "./Navbar";
import { Footer } from "./Footer";
import { CompactFooter } from "./CompactFooter";
import { FooterBand } from "./FooterBand";
import { BottomNavigation } from "./BottomNavigation";
import { PublicSidebar } from "./PublicSidebar";
import { PublicMobileHeader } from "./PublicMobileHeader";
import { PublicMobileBottomBar } from "./PublicMobileBottomBar";
import MusicPlayer from "../music/MusicPlayer";
import { ProfileCompletenessProvider } from "@/contexts/ProfileCompletenessContext";
import { Toaster } from "../ui/toaster";
import { CookieConsentModal } from "../legal/CookieConsentModal";
import { PWAInstallPrompt } from "../pwa/PWAInstallPrompt";
import { NetworkStatus } from "../pwa/NetworkStatus";
import { FloatingVerificationBubble } from "@/components/verification/FloatingVerificationBubble";
import {
  VerificationUpgradeBanner,
  useVerificationBannerVisible,
} from "@/components/verification/VerificationUpgradeBanner";
import {
  AnnouncementBanner,
  useAnnouncementBannerVisible,
} from "./AnnouncementBanner";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useIsMobile, useIsTablet } from "@/hooks/useResponsive";
import { cn } from "@/lib/utils";
import AdminMilestoneTrigger from "@/components/admin/tools/AdminMilestoneTrigger";
import { GlobalArtistOnboardingChecklist } from "@/components/onboarding/GlobalArtistOnboardingChecklist";
import {
  usesSidebarShell,
  usesDashboardShell,
  getSidebarSection,
  FOOTER_BAND_ROUTES,
} from "@/utils/sidebarShellRoutes";
import {
  SidebarActiveProvider,
  useSidebarActiveContext,
} from "@/contexts/SidebarActiveContext";

interface LayoutProps {
  children: ReactNode;
}

// Rendered outside each page's Suspense boundary (via Layout) so the rail
// nav never unmounts during a route's code-split load — only the center
// content column shows a loading state while this stays put.
function SidebarShellAside({ pathname }: { pathname: string }) {
  const { overrideActive } = useSidebarActiveContext();
  const section = overrideActive ?? getSidebarSection(pathname);
  // Pages on this shell with no matching rail item (e.g. legal pages) still
  // get the sidebar — it just renders with nothing highlighted.
  return <PublicSidebar active={section ?? undefined} />;
}

// Determine which footer type to show based on route
const getFooterType = (pathname: string): "full" | "compact" | "none" => {
  // No footer on login/register (including the per-role register screens), or
  // on any page using the redesigned sidebar shell — those carry their own
  // footer band (or none, per their mockup). Same for the bare "/dashboard"
  // redirect route: it never renders real content, just bounces the user to
  // their role's dashboard, so it should carry no chrome at all.
  if (
    pathname.startsWith("/login") ||
    pathname.startsWith("/register") ||
    pathname === "/forgot-password" ||
    pathname === "/reset-password" ||
    pathname === "/verify-email" ||
    pathname === "/dashboard" ||
    usesSidebarShell(pathname) ||
    usesDashboardShell(pathname)
  )
    return "none";

  // Main site pages get full footer
  const mainSiteRoutes = ["/about", "/faq", "/community"];

  // Exact match for main routes
  if (mainSiteRoutes.includes(pathname)) return "full";

  // Everything else (albums, dashboards, etc.) = compact
  return "compact";
};

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { showConsent, acceptCookies, declineCookies } = useCookieConsent();
  const isBannerVisible = useAnnouncementBannerVisible();
  const isVerificationBannerVisible = useVerificationBannerVisible();

  // Hide navbar on login, register, and forgot/reset-password pages (including
  // the per-role register screens), and on any page using the redesigned
  // sidebar shell — its rail nav replaces the top navbar.
  const isLoginPage = location.pathname.startsWith("/login");
  const isRegisterPage = location.pathname.startsWith("/register");
  const isForgotPasswordPage = location.pathname === "/forgot-password";
  const isResetPasswordPage = location.pathname === "/reset-password";
  const isVerifyAccountPage = location.pathname === "/verify-email";
  const isImmersiveAuthPage =
    isLoginPage ||
    isRegisterPage ||
    isForgotPasswordPage ||
    isResetPasswordPage ||
    isVerifyAccountPage;
  const isPublicSidebarPage = usesSidebarShell(location.pathname);
  const isDashboardShellPage = usesDashboardShell(location.pathname);
  const isSidebarShellPage = isPublicSidebarPage || isDashboardShellPage;
  // The bare "/dashboard" route never shows real content — RoleBasedRouter
  // renders it purely to redirect to the signed-in user's actual dashboard
  // (from Login, registration, and the OAuth callback). Flashing the classic
  // navbar/footer around that blank instant reads as the old, pre-redesign
  // shell appearing before the new dashboard loads, so it gets no chrome.
  const isRoleRedirectPage = location.pathname === "/dashboard";
  const shouldShowNavbar =
    !isImmersiveAuthPage && !isSidebarShellPage && !isRoleRedirectPage;
  const showsBottomNav = !isDashboardShellPage && !isRoleRedirectPage;
  // Pages that render PublicMobileBottomBar (full-width bar on mobile,
  // floating dock on tablet) — either directly via showsBottomNav below, or
  // (for Fan/Artist/Sponsor dashboards + Support Tickets) embedded in their
  // own content. Edit Profile is the one dashboard-shell route without it.
  const hasFloatingBottomNav =
    isPublicSidebarPage ||
    (isDashboardShellPage && location.pathname !== "/profile");

  // Determine footer type
  const footerType = getFooterType(location.pathname);

  // These sidebar-shell pages render the shared full-bleed FooterBand (dark,
  // flush to the page edge) as the last element of their scrollable content
  // instead of the classic Footer/CompactFooter, so they shouldn't get the
  // generic bottom padding below — that would leave a visible gap of page
  // background between the footer band and the true bottom of the viewport.
  const showsFooterBand = FOOTER_BAND_ROUTES.has(location.pathname);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Bottom clearance for the floating mobile/tablet nav + player, expressed
  // as Tailwind responsive classes on the root wrapper below rather than an
  // effect that writes document.body.style.paddingBottom directly. The
  // previous version set this imperatively from useIsMobile/useIsTablet, two
  // separate hooks each with their own resize/matchMedia listener — if a
  // resize landed between their two updates (or any effect-ordering edge
  // case left the cleanup's "reset to 0px" racing the next render's value),
  // the inline style could get stuck at the wrong breakpoint's value until a
  // full reload reset it. A plain class string has no such window: the
  // browser's own CSS engine re-evaluates md:/lg: on every reflow, so it
  // can't go stale independently of what's actually on screen.
  const bottomPaddingClass = showsFooterBand
    ? // This page's own <main> bottom padding (see getMainPadding below)
      // already reserves exactly enough room above its mt-auto-pinned
      // footer band for the floating bottom nav to sit without covering
      // it — stacking this generic wrapper padding on top of that would
      // reopen the same gap the main-padding fix exists to close.
      "pb-0"
    : isPublicSidebarPage && showsBottomNav
      ? // Mobile bottom nav (64px) + player (64px) + margins (16px); tablet's
        // floating dock + margin; desktop needs none.
        "pb-36 md:pb-[120px] lg:pb-0"
      : "pb-36 md:pb-0";

  // Calculate top padding based on navbar and banner visibility
  const getMainPadding = () => {
    if (!shouldShowNavbar) {
      if (isMobile && isImmersiveAuthPage) {
        return "pt-0 pb-0 overflow-hidden h-screen";
      }
      // Skip overflow-x-hidden on sidebar-shell pages: setting either overflow
      // axis to anything but "visible" forces the browser to compute the
      // *other* axis as "auto" too (CSS overflow spec), which turns <main>
      // into its own scroll container. The rail sidebar is position:sticky,
      // and since <main> never actually scrolls internally (the window
      // does), that sticky positioning ends up scoped to a container that
      // never moves — the sidebar just scrolls away instead of pinning to
      // the viewport.
      // The footer band sits flush against the bottom of the page (pinned
      // there via mt-auto when content falls short of the viewport) —
      // without clearance here, PublicMobileBottomBar's floating
      // mobile/tablet nav would sit directly on top of it and cover the
      // footer's last row. Sized to the bar's actual footprint (56px bar +
      // safe-area inset on mobile; 64px dock + 24px margin on tablet) so
      // there's no left-over gap below the footer.
      const bottomPadding = showsFooterBand
        ? "pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-[88px] lg:pb-0"
        : "pb-4";
      return isSidebarShellPage
        ? `pt-0 ${bottomPadding}`
        : `pt-0 ${bottomPadding} overflow-x-hidden`;
    }
    // When navbar is shown: 64px navbar + 40px per banner
    // On mobile, verification banner is a modal overlay so doesn't affect padding
    const verificationBannerAffectsPadding =
      isVerificationBannerVisible && !isMobile;
    const both = isBannerVisible && verificationBannerAffectsPadding;
    const either = isBannerVisible || verificationBannerAffectsPadding;
    const paddingClass = both ? "pt-[144px]" : either ? "pt-[104px]" : "pt-16";
    return `${paddingClass} pb-4 overflow-x-hidden`;
  };

  return (
    <ProfileCompletenessProvider>
      <TooltipProvider>
        <div
          className={`min-h-screen flex flex-col bg-background text-foreground ${bottomPaddingClass} ${isSidebarShellPage ? "" : "overflow-x-hidden"}`}
        >
          {shouldShowNavbar && (
            <>
              <AnnouncementBanner />
              <Navbar />
              <VerificationUpgradeBanner />
            </>
          )}

          {/* Network Status Indicator - Only show on desktop */}
          {!isMobile && (
            <div className="fixed top-20 right-4 z-50">
              <NetworkStatus showText={true} />
            </div>
          )}

          {(() => {
            const mainSection = isPublicSidebarPage ? (
              <>
                <PublicMobileHeader />
                <div className="light flex flex-1 w-full bg-white font-archivo">
                  <SidebarShellAside pathname={location.pathname} />
                  <main className={cn("flex-1 min-w-0", getMainPadding())}>
                    {children}
                    {showsFooterBand && <FooterBand />}
                  </main>
                </div>
              </>
            ) : (
              <main className={cn("flex-1 w-full", getMainPadding())}>
                {children}
              </main>
            );

            // Hide all sections below main for mobile login/register/forgot-password page
            const belowMainSection = !(isMobile && isImmersiveAuthPage) && (
              <>
                {/* Music Player - Let PlayerContainer handle its own positioning.
                    On sidebar-shell pages the mobile bottom nav is its own
                    full-width bar (56px, plus the device's safe-area inset),
                    so the player needs its own clearance to avoid sitting
                    under it. On tablet, PublicSidebar's own dock
                    floats centered and narrow — lifting the full-width player
                    above it left a dead gap on both sides, so that dock lifts
                    itself above the player instead (PublicMobileBottomBar)
                    and the player stays flush with the screen's bottom edge.
                    Dashboard-shell pages (Artist/Fan/Sponsor dashboards) keep
                    their previous tablet clearance — untouched here. */}
                <MusicPlayer
                  mobileBottomOffsetClass={
                    hasFloatingBottomNav
                      ? isPublicSidebarPage
                        ? "bottom-[56px]"
                        : "bottom-16"
                      : undefined
                  }
                  tabletBottomOffsetClass={
                    hasFloatingBottomNav && !isPublicSidebarPage
                      ? "bottom-[100px]"
                      : undefined
                  }
                  desktopSidebarOffsetClass={
                    isPublicSidebarPage ? "lg:left-[264px]" : undefined
                  }
                />

                {/* Bottom Navigation - not on role dashboards, which have their own mobile nav */}
                {showsBottomNav &&
                  (isPublicSidebarPage
                    ? (isMobile || isTablet) && <PublicMobileBottomBar />
                    : isMobile && <BottomNavigation />)}

                {/* Desktop Footer - Full or Compact based on route */}
                {!isMobile && footerType !== "none" && (
                  <div className="mt-auto">
                    {footerType === "full" ? <Footer /> : <CompactFooter />}
                  </div>
                )}

                {/* PWA Install Prompt */}
                <PWAInstallPrompt />

                {/* Floating Verification Bubble */}
                <FloatingVerificationBubble />
              </>
            );

            return isPublicSidebarPage ? (
              <SidebarActiveProvider>
                {mainSection}
                {belowMainSection}
              </SidebarActiveProvider>
            ) : (
              <>
                {mainSection}
                {belowMainSection}
              </>
            );
          })()}

          <CookieConsentModal
            isOpen={showConsent}
            onAccept={acceptCookies}
            onDecline={declineCookies}
          />
        </div>
        {/* Admin one-off utilities mounted globally */}
        <AdminMilestoneTrigger />
        <GlobalArtistOnboardingChecklist />
        <Toaster />
      </TooltipProvider>
    </ProfileCompletenessProvider>
  );
}
